import {useMemo} from 'react'
import {createUseStyles} from 'react-jss'
import {Colors} from "../../constants/colors"
import {Employee, EmployeeStatus} from "../../redux/types"
import {formatDateString} from '../../util/date'
import ListItemCard from "../ListItemCard"
import Pagination from '../Pagination'
import Badge from '../Badge'
import {useTranslation} from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface Props {
  employees: Employee[]
  pageIndex: number
  pageSize: number
  onPageChange: (pageIndex: number) => void
}

const EmployeeList = ({employees, pageIndex, pageSize, onPageChange}: Props) => {
  const styles = useStyles()
  const {t} = useTranslation(['common', 'employees']);
  const navigate = useNavigate()

  const itemsOnCurrentPage = useMemo(() => {
    const firstPageIndex = (pageIndex - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return employees.slice(firstPageIndex, lastPageIndex);
  }, [employees, pageIndex, pageSize])

  const renderHeader = () => {
    return (
      <ListItemCard className={styles.listItemContainer}>
        <div className={styles.smallCellContainer}>
          <strong>{t('id', {ns: 'common'})}</strong>
        </div>
        <div className={styles.smallCellContainer}>
          <strong>{t('employmentNumber', {ns: 'employees'})}</strong>
        </div>
        <div className={styles.defaultCellContainer}>
          <strong>{t('idNumber', {ns: 'common'})}</strong>
        </div>
        <div className={styles.nameCellContainer}>
          <strong>{t('name', {ns: 'common'})}</strong>
        </div>
        <div className={styles.defaultCellContainer}>
          <strong>{t('category', {ns: 'common'})}</strong>
        </div>
        <div className={styles.defaultCellContainer}>
          <strong>{t('department', {ns: 'common'})}</strong>
        </div>
        <div className={styles.defaultCellContainer}>
          <strong>{t('activationDate', {ns: 'common'})}</strong>
        </div>
        <div className={styles.statusContainer}>
          <strong>{t('status', {ns: 'common'})}</strong>
        </div>
        <div className={styles.statusContainer}>
          <strong>{t('flags', {ns: 'common'})}</strong>
        </div>
      </ListItemCard>
    )
  }

  const renderListItems = () => {
    return itemsOnCurrentPage?.map((employee, index) => {
      return (
        <ListItemCard key={`employee${index}`} className={styles.listItemContainer} index={index} onClick={() => navigate(`/employees/${employee.id}`)}>
          <div className={styles.smallCellContainer}>
            {employee.externalId}
          </div>
          <div className={styles.smallCellContainer}>
            {employee.employmentNumber}
          </div>
          <div className={styles.defaultCellContainer}>
            {employee.identificationNumber}
          </div>
          <div className={styles.nameCellContainer}>
            {employee.givenName} {employee.familyName}
          </div>
          <div className={styles.defaultCellContainer}>
            {employee.category}
          </div>
          <div className={styles.defaultCellContainer}>
            {employee.department}
          </div>
          <div className={styles.defaultCellContainer}>
            {formatDateString(employee.activationDate)}
          </div>
          <div className={styles.statusContainer}>
            {renderStatusBadge(employee)}
          </div>
          <div className={styles.statusContainer}>
            {renderFlagBadges(employee)}
          </div>
        </ListItemCard>
      )
    })
  }

  const renderStatusBadge = (employee: Employee) => {
    return (
      <Badge
        text={t(`employeeStatus_${employee.status}`, {ns: 'employees'})}
        backgroundColor={getStatusColor(employee.status)}
        textColor={getStatusTextColor(employee.status)}
        tooltip={t(`employeeStatusDescription_${employee.status}`, {ns: 'employees'})}
      />
    )
  }

  const getStatusColor = (status: EmployeeStatus) => {
    switch (status) {
      case 'registered':
        return Colors.euro_50
      case 'connected':
        return Colors.euro_300
      case 'active':
        return Colors.euro_800
      case 'paused':
        return Colors.yen_300
      case 'terminated':
        return Colors.yuan_100
    }
  }

  const getStatusTextColor = (status: EmployeeStatus) => {
    switch (status) {
      case 'registered':
        return Colors.gray0
      case 'connected':
        return Colors.gray0
      case 'active':
        return Colors.gray4
      case 'paused':
        return Colors.gray0
      case 'terminated':
        return Colors.rubel_700
    }
  }

  const renderFlagBadges = (employee: Employee) => {
    return (
      <>
        {employee.underForeclosure &&
            <Badge
                text={t('underForeclosure', {ns: 'employees'})}
                backgroundColor={Colors.gray3}
                textColor={Colors.gray4}
            />
        }
        {employee.nonEligible &&
            <Badge
                text={t('nonEligible', {ns: 'employees'})}
                backgroundColor={Colors.gray3}
                textColor={Colors.gray4}
            />
        }
        {(employee.payoutSettings?.maxAmountPerPayday != null || employee.payoutSettings?.maxPercentagePerPayday != null || employee.payoutSettings?.maxCustomPercentagePerPayday != null) &&
            <Badge
                text={t('payoutLimitation', {ns: 'employees'})}
                backgroundColor={Colors.gray3}
                textColor={Colors.gray4}
            />
        }
        {employee.hasUnsettledPayouts &&
            <Badge
                text={t('unsettledPayouts', {ns: 'employees'})}
                backgroundColor={Colors.gray3}
                textColor={Colors.gray4}
            />
        }
      </>
    )
  }

  return (
    <div>
      <div className={styles.container}>
        {renderHeader()}
        {renderListItems()}
      </div>
      <div className={styles.pagination}>
        <Pagination
          pageIndex={pageIndex}
          itemCount={employees.length}
          pageSize={pageSize}
          siblingCount={1}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  )
}

export default EmployeeList

const useStyles = createUseStyles({
  container: {
    width: '100%',
    overflowX: 'auto',
  },
  listItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.grayBackgroundStrong,
    }
  },
  smallCellContainer: {
    flexShrink: 0,
    width: 100
  },
  defaultCellContainer: {
    flexShrink: 0,
    width: 140
  },
  nameCellContainer: {
    flexShrink: 0,
    width: 200
  },
  statusContainer: {
    flexShrink: 0,
    flexGrow: 1,
    display: 'flex',
    width: 120,
    flexWrap: 'wrap',
  },
  button: {
    marginRight: 15,
    transition: '0.4s',
    '&:hover': {
      color: Colors.euro_600,
      cursor: 'pointer',
    }
  },
  pagination: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center'
  }
})
